/**
 * PricingTemplatesSettings - Settings page for managing pricing templates
 *
 * Displays all pricing templates in a sortable table with CRUD operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Spinner, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import { db } from 'config/firebase';
import { collection, collectionGroup, query, where, orderBy, onSnapshot, deleteDoc, doc, getDocs, addDoc, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faSort, faSortUp, faSortDown, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { PricingTemplateModal } from './PricingTemplateModal';
import { formatCurrency } from '../newProducts/utils/pricingCalculations';

/**
 * Settings component for viewing and managing pricing templates.
 */
export const PricingTemplatesSettings = () => {
  const navigate = useNavigate();

  // Data state
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productCounts, setProductCounts] = useState({}); // Map of templateId -> count

  // Sort state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Default toggle state
  const [settingDefault, setSettingDefault] = useState(null); // templateId being set as default

  // Load templates from Firestore with real-time updates
  useEffect(() => {
    const q = query(
      collection(db, 'systemTemplates'),
      where('type', '==', 'pricing'),
      orderBy('timestamp.created', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const templateData = [];
        querySnapshot.forEach((docSnap) => {
          templateData.push({ id: docSnap.id, ...docSnap.data() });
        });
        setTemplates(templateData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading templates:', error);
        toast.error('Failed to load pricing templates', { theme: 'colored' });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load product counts for each template
  const loadProductCounts = useCallback(async (templateIds) => {
    const counts = {};
    for (const templateId of templateIds) {
      try {
        const q = query(
          collectionGroup(db, 'variants'),
          where('pricingTemplateId', '==', templateId)
        );
        const snapshot = await getDocs(q);
        counts[templateId] = snapshot.size;
      } catch (error) {
        console.error(`Error counting products for template ${templateId}:`, error);
        counts[templateId] = 0;
      }
    }
    setProductCounts(counts);
  }, []);

  // Refresh product counts when templates change
  useEffect(() => {
    if (templates.length > 0) {
      const templateIds = templates.map(t => t.id);
      loadProductCounts(templateIds);
    }
  }, [templates, loadProductCounts]);

  // Sort templates locally
  const sortedTemplates = [...templates].sort((a, b) => {
    let aVal, bVal;

    switch (sortField) {
      case 'name':
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
        break;
      case 'description':
        aVal = (a.description || '').toLowerCase();
        bVal = (b.description || '').toLowerCase();
        break;
      case 'marginType':
        aVal = a.settings?.marginType || '';
        bVal = b.settings?.marginType || '';
        break;
      case 'prefMargin':
        aVal = a.settings?.prefMargin || 0;
        bVal = b.settings?.prefMargin || 0;
        break;
      case 'additionalFulfillmentCost':
        aVal = a.settings?.additionalFulfillmentCost || 0;
        bVal = b.settings?.additionalFulfillmentCost || 0;
        break;
      case 'roundingEnding':
        aVal = a.settings?.roundingEnding || 0;
        bVal = b.settings?.roundingEnding || 0;
        break;
      case 'minimumPrice':
        aVal = a.settings?.minimumPrice || 0;
        bVal = b.settings?.minimumPrice || 0;
        break;
      default:
        aVal = a.name || '';
        bVal = b.name || '';
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle column header click for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted" />;
    }
    return sortDirection === 'asc'
      ? <FontAwesomeIcon icon={faSortUp} className="ms-1" />
      : <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
  };

  // Open modal for editing
  const handleRowClick = (template) => {
    setSelectedTemplate(template);
    setShowModal(true);
  };

  // Open modal for creating
  const handleCreate = () => {
    setSelectedTemplate(null);
    setShowModal(true);
  };

  // Handle modal close
  const handleModalHide = () => {
    setShowModal(false);
    setSelectedTemplate(null);
  };

  // Handle template saved
  const handleSave = useCallback(() => {
    // Templates reload via real-time listener, just close modal for create
    if (!selectedTemplate) {
      setShowModal(false);
    }
  }, [selectedTemplate]);

  // Handle delete confirmation
  const handleDeleteClick = (e, templateId) => {
    e.stopPropagation(); // Prevent row click
    setDeleteConfirmId(templateId);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (e, templateId) => {
    e.stopPropagation(); // Prevent row click
    try {
      await deleteDoc(doc(db, 'systemTemplates', templateId));
      toast.success('Template deleted', { theme: 'colored' });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template', { theme: 'colored' });
    }
    setDeleteConfirmId(null);
  };

  // Handle delete cancel
  const handleDeleteCancel = (e) => {
    e.stopPropagation(); // Prevent row click
    setDeleteConfirmId(null);
  };

  // Handle default toggle
  const handleSetDefault = async (e, templateId) => {
    e.stopPropagation(); // Prevent row click
    setSettingDefault(templateId);

    try {
      const clickedTemplate = templates.find(t => t.id === templateId);
      const wasDefault = clickedTemplate?.isDefault || false;
      const previousDefault = templates.find(t => t.isDefault && t.id !== templateId);

      const batch = writeBatch(db);

      // If clicking already-default template, remove default
      if (wasDefault) {
        batch.update(doc(db, 'systemTemplates', templateId), { isDefault: false });
      } else {
        // Remove previous default if exists
        if (previousDefault) {
          batch.update(doc(db, 'systemTemplates', previousDefault.id), { isDefault: false });
        }
        // Set clicked template as default
        batch.update(doc(db, 'systemTemplates', templateId), { isDefault: true });
      }

      await batch.commit();
      toast.success('Default template updated', { theme: 'colored' });
    } catch (error) {
      console.error('Error setting default template:', error);
      toast.error('Failed to update default template', { theme: 'colored' });
    } finally {
      setSettingDefault(null);
    }
  };

  // Handle duplicate
  const handleDuplicate = async (e, template) => {
    e.stopPropagation(); // Prevent row click

    try {
      const duplicateData = {
        name: `Copy of ${template.name}`,
        description: template.description || '',
        settings: { ...template.settings },
        type: 'pricing',
        isDefault: false, // Duplicates are never default
        timestamp: {
          created: serverTimestamp(),
          updated: serverTimestamp()
        }
      };

      await addDoc(collection(db, 'systemTemplates'), duplicateData);
      toast.success('Template duplicated', { theme: 'colored' });
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template', { theme: 'colored' });
    }
  };

  // Handle product count click - navigate to filtered products
  const handleProductCountClick = (e, templateId) => {
    e.stopPropagation(); // Prevent row click
    navigate(`/products?pricingTemplateId=${templateId}`);
  };

  // Truncate text with tooltip
  const TruncatedText = ({ text, maxLength = 50 }) => {
    if (!text) return <span className="text-muted">-</span>;
    if (text.length <= maxLength) return <span>{text}</span>;

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{text}</Tooltip>}
      >
        <span>{text.substring(0, maxLength)}...</span>
      </OverlayTrigger>
    );
  };

  // Format margin type as badge
  const MarginTypeBadge = ({ type }) => {
    if (type === 'markup') {
      return <Badge bg="info">Markup</Badge>;
    }
    return <Badge bg="success">Margin</Badge>;
  };

  // Format percentage display
  const formatPercent = (decimal) => {
    if (decimal === null || decimal === undefined) return '-';
    return `${(decimal * 100).toFixed(1)}%`;
  };

  // Format rounding ending display
  const formatRounding = (ending) => {
    if (ending === null || ending === undefined) return '-';
    return `.${String(ending).split('.')[1] || '00'}`;
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Pricing Templates</h5>
        <Button variant="primary" size="sm" onClick={handleCreate}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          New Template
        </Button>
      </Card.Header>

      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">No pricing templates yet</p>
            <Button variant="primary" onClick={handleCreate}>
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Create Your First Template
            </Button>
          </div>
        ) : (
          <Table hover responsive className="mb-0 table-sm">
            <thead className="table-light">
              <tr>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('name')}
                >
                  Name {getSortIcon('name')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('description')}
                >
                  Description {getSortIcon('description')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('marginType')}
                >
                  Type {getSortIcon('marginType')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('prefMargin')}
                >
                  % {getSortIcon('prefMargin')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('additionalFulfillmentCost')}
                >
                  Fulfillment {getSortIcon('additionalFulfillmentCost')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('roundingEnding')}
                >
                  Rounding {getSortIcon('roundingEnding')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('minimumPrice')}
                >
                  Min Floor {getSortIcon('minimumPrice')}
                </th>
                <th>Products</th>
                <th style={{ width: '80px' }}>Default</th>
                <th style={{ width: '90px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTemplates.map((template) => (
                <tr
                  key={template.id}
                  onClick={() => handleRowClick(template)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{template.name}</strong>
                  </td>
                  <td>
                    <TruncatedText text={template.description} />
                  </td>
                  <td>
                    <MarginTypeBadge type={template.settings?.marginType} />
                  </td>
                  <td>
                    {formatPercent(template.settings?.prefMargin)}
                  </td>
                  <td>
                    {template.settings?.additionalFulfillmentCost != null
                      ? formatCurrency(template.settings.additionalFulfillmentCost)
                      : '-'}
                  </td>
                  <td>
                    {formatRounding(template.settings?.roundingEnding)}
                  </td>
                  <td>
                    {template.settings?.minimumPrice != null
                      ? formatCurrency(template.settings.minimumPrice)
                      : '-'}
                  </td>
                  <td>
                    {(productCounts[template.id] || 0) > 0 ? (
                      <span
                        className="text-primary"
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={(e) => handleProductCountClick(e, template.id)}
                        title="View products using this template"
                      >
                        {productCounts[template.id]}
                      </span>
                    ) : (
                      <span className="text-muted">0</span>
                    )}
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="switch"
                      id={`default-${template.id}`}
                      checked={template.isDefault || false}
                      onChange={() => {}}
                      onClick={(e) => handleSetDefault(e, template.id)}
                      disabled={settingDefault === template.id}
                      title={template.isDefault ? 'Remove default' : 'Set as default'}
                    />
                  </td>
                  <td>
                    {deleteConfirmId === template.id ? (
                      <div className="d-flex gap-1">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => handleDeleteConfirm(e, template.id)}
                          title="Confirm delete"
                        >
                          Yes
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleDeleteCancel}
                          title="Cancel"
                        >
                          No
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={(e) => handleDuplicate(e, template)}
                          title="Duplicate template"
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => handleDeleteClick(e, template.id)}
                          title="Delete template"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Create/Edit Modal */}
      <PricingTemplateModal
        show={showModal}
        onHide={handleModalHide}
        template={selectedTemplate}
        onSave={handleSave}
      />
    </Card>
  );
};

export default PricingTemplatesSettings;
